import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { LibraryItem } from '../Library/libraryData';

import { useTranslation } from 'react-i18next';

interface OptionsBoxProps {
  title: string;
  icon: React.ReactNode;
  items: LibraryItem[];
  onOptionSelect?: (item: LibraryItem) => void;
  onDashboardNavigate?: (item: LibraryItem) => void;
  onClose?: () => void;
}

export const OptionsBox: React.FC<OptionsBoxProps> = ({
  title,
  icon,
  items,
  onOptionSelect,
  onDashboardNavigate,
  onClose,
}) => {
  const { t } = useTranslation('pleri/chat');
  const boxRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    onClose?.();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleOptionClick = (item: LibraryItem) => {
    // If item has dashboardId or dashboardTitleMatch, navigate to dashboard
    if (item.dashboardId || item.dashboardTitleMatch) {
      onDashboardNavigate?.(item);
    } else {
      onOptionSelect?.(item);
    }
  };

  const titleId = `options-box-title-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div
      ref={boxRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="animate-fadeInSlide mx-auto mt-4 mb-4 w-[95%] max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 text-gray-600 dark:text-gray-400">{icon}</div>
          <span
            id={titleId}
            className="text-xs font-medium text-gray-400 dark:text-gray-500"
          >
            {title}
          </span>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="h-4 w-4 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-400"
          aria-label={t('close')}
        >
          <X size={14} />
        </button>
      </div>
      <div>
        {items.map((item, index) => (
          <React.Fragment key={item.id}>
            {index > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700" />
            )}
            <button
              type="button"
              onClick={() => handleOptionClick(item)}
              className="w-full px-4 py-3 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <div className="font-medium">{item.title}</div>
              {item.description && (
                <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {item.description}
                </div>
              )}
            </button>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
